import { formatDate } from 'commons/utils/date'

import type {
  FairShareBMUByMaterialPayload,
  FairShareBMUByMaterialPayloadApi,
  FairShareBMUPayload,
  FairShareBMUPayloadApi,
  FairShareBMUResponse,
  FairShareBMUResponseApi,
  FairShareByMaterialResponse,
  FairShareByMaterialResponseApi,
  FairShareOfficeByMaterialPayload,
  FairShareOfficeByMaterialPayloadApi,
  FairShareOfficeDownload,
  FairShareOfficeDownloadApi,
  FairShareOfficePayload,
  FairShareOfficePayloadApi,
  FairShareOfficeResponse,
  FairShareOfficeResponseApi,
  FairShareOpenWalletPayload,
  FairShareOpenWalletPayloadApi,
  FairShareTrackingPayload,
  FairShareTrackingPayloadApi,
  FairShareTrackingResponse,
  FairShareTrackingResponseApi,
  MaterialLevelPayload,
  MaterialLevelPayloadApi,
  MaterialSharePayload,
  MaterialSharePayloadApi,
  MaterialShareResponse,
  MaterialShareResponseApi,
} from './interfaces'

export const toFairShareBMUPayloadApi = (
  fairSharePayload: FairShareBMUPayload,
  isDownload?: boolean,
): FairShareBMUPayloadApi => {
  return {
    AnoMes: fairSharePayload.yearMonth,
    CodigoCentro: fairSharePayload.centerCode,
    CodigoCor: fairSharePayload.colorCodes,
    CodigoMaterial: fairSharePayload.materialCodes,
    PaginaAtual: isDownload ? undefined : fairSharePayload.currentPage,
    RegistrosPorPagina: isDownload ? undefined : fairSharePayload.pageSize,
    UnidadeNegocio: fairSharePayload.businessUnit,
  }
}

export const toFairShareBMUResponse = (
  fairShareResponseApi: FairShareBMUResponseApi,
): FairShareBMUResponse => {
  return {
    businessUnits: fairShareResponseApi.unidadesNegocio.map((item) => ({
      code: item.codigo,
      description: item.descricao,
    })),
    data: fairShareResponseApi.dados.map((item) => ({
      approved: item.aprovado,
      availableBalance: item.saldoDisponivel,
      center: item.centro,
      code: item.codigo,
      description: item.descricao,
      share: item.share,
      target: item.meta,
      typed: item.digitado,
    })),
    totals: {
      availableBalance: fairShareResponseApi.totalizadores.saldoDisponivel,
      consideredQuantity: fairShareResponseApi.totalizadores.quantidadeConsiderada,
      rowsCount: fairShareResponseApi.totalizadores.rowscount,
      share: fairShareResponseApi.totalizadores.share,
      totalApprovedQuantity: fairShareResponseApi.totalizadores.quantidadeTotalAprovada,
      totalCancelledQuantity: fairShareResponseApi.totalizadores.quantidadeTotalCancelada,
      totalSalesPlanQuantity: fairShareResponseApi.totalizadores.quantidadeTotalPlanoVenda,
      totalTargetQuantity: fairShareResponseApi.totalizadores.quantidadeTotalMeta,
      totalTypedQuantity: fairShareResponseApi.totalizadores.quantidadeTotalDigitada,
    },
  }
}

export const toFairShareBMUByMaterialPayloadApi = (
  fairShareByMaterialPayload: FairShareBMUByMaterialPayload,
): FairShareBMUByMaterialPayloadApi => {
  return {
    AnoMes: fairShareByMaterialPayload.yearMonth,
    CodigoCentro: fairShareByMaterialPayload.centerCode,
    CodigoMaterial: fairShareByMaterialPayload.materialCode,
    UnidadeNegocio: fairShareByMaterialPayload.businessUnit,
  }
}

export const toFairShareByMaterialResponse = (
  fairShareByMaterialResponseApi: FairShareByMaterialResponseApi[],
): FairShareByMaterialResponse[] => {
  return fairShareByMaterialResponseApi.map((item) => ({
    approved: item.aprovado,
    availableBalance: item.saldoDisponivel,
    code: item.codigo,
    description: item.descricao,
    extrapolationPercentage: item.percentualExtrapolacao,
    share: item.share,
    target: item.meta,
    typed: item.digitado,
  }))
}

export const toFairShareOfficePayloadApi = (
  fairShareOfficePayload: FairShareOfficePayload,
): FairShareOfficePayloadApi => {
  return {
    anoMes: fairShareOfficePayload.yearMonth,
    codigoCentro: fairShareOfficePayload.centerCode,
    codigoCor: fairShareOfficePayload.colorCodes,
    codigoEquipeVenda: fairShareOfficePayload.teamCode,
    codigoEscritorioVenda: fairShareOfficePayload.officeCode,
    codigoMaterial: fairShareOfficePayload.materialCodes,
    codigoRegiaoVenda: fairShareOfficePayload.regionCode,
    paginaAtual: fairShareOfficePayload.currentPage,
    registrosPorPagina: fairShareOfficePayload.pageSize,
    unidadeNegocio: fairShareOfficePayload.businessUnit,
  }
}

export const toFairShareOfficeResponse = (
  fairShareOfficeResponseApi: FairShareOfficeResponseApi,
): FairShareOfficeResponse => {
  return {
    data: fairShareOfficeResponseApi.dados.map((item) => ({
      approved: item.aprovado,
      availableBalance: item.saldoDisponivel,
      center: item.centro,
      code: item.codigo,
      description: item.descricao,
      selectedLevel: item.nivelSelecionado,
      share: item.share,
      target: item.meta,
      typed: item.digitado,
    })),
    offices: fairShareOfficeResponseApi.escritorios.map((item) => ({
      frozenTarget: item.metaCongelada,
      hasCalendar: item.temCalendario,
      officeCode: item.codigoEscritorioVenda,
    })),
    totals: {
      availableBalance: fairShareOfficeResponseApi.totalizadores.saldoDisponivel,
      consideredQuantity: fairShareOfficeResponseApi.totalizadores.quantidadeConsiderada,
      rowsCount: fairShareOfficeResponseApi.totalizadores.rowscount,
      share: fairShareOfficeResponseApi.totalizadores.share,
      totalApprovedQuantity: fairShareOfficeResponseApi.totalizadores.quantidadeTotalAprovada,
      totalCancelledQuantity: fairShareOfficeResponseApi.totalizadores.quantidadeTotalCancelada,
      totalSalesPlanQuantity: fairShareOfficeResponseApi.totalizadores.quantidadeTotalPlanoVenda,
      totalTargetQuantity: fairShareOfficeResponseApi.totalizadores.quantidadeTotalMeta,
      totalTypedQuantity: fairShareOfficeResponseApi.totalizadores.quantidadeTotalDigitada,
    },
  }
}

export const toFairShareOfficeByMaterialPayloadApi = (
  fairShareOfficeByMaterialPayload: FairShareOfficeByMaterialPayload,
): FairShareOfficeByMaterialPayloadApi => {
  return {
    anoMes: fairShareOfficeByMaterialPayload.yearMonth,
    codigoCentro: fairShareOfficeByMaterialPayload.centerCode,
    codigoMaterial: fairShareOfficeByMaterialPayload.materialCode,
    equipeVenda: fairShareOfficeByMaterialPayload.teamCode,
    escritorioVenda: fairShareOfficeByMaterialPayload.officeCode,
    regiaoVenda: fairShareOfficeByMaterialPayload.regionCode,
    unidadeNegocio: fairShareOfficeByMaterialPayload.businessUnit,
  }
}

export const toFairShareOpenWalletPayloadApi = (
  fairShareOpenWalletPayload: FairShareOpenWalletPayload,
): FairShareOpenWalletPayloadApi => {
  return {
    anoMes: fairShareOpenWalletPayload.yearMonth,
    codigoEscritorio: fairShareOpenWalletPayload.officeCode,
    codigoUnidadeNegocio: fairShareOpenWalletPayload.businessUnitCode,
  }
}

export const toFairShareOfficeDownloadPayloadApi = (
  fairShareOfficeDownloadPayload: FairShareOfficeDownload,
): FairShareOfficeDownloadApi => {
  return {
    AnoMes: fairShareOfficeDownloadPayload.yearMonth,
    CodigoCentro: fairShareOfficeDownloadPayload.centerCode,
    CodigoCor: fairShareOfficeDownloadPayload.colorCodes,
    CodigoMaterial: fairShareOfficeDownloadPayload.materialCodes,
    EquipeVenda: fairShareOfficeDownloadPayload.teamCode,
    EscritorioVenda: fairShareOfficeDownloadPayload.officeCode,
    RegiaoVenda: fairShareOfficeDownloadPayload.regionCode,
    UnidadeNegocio: fairShareOfficeDownloadPayload.businessUnit,
  }
}

export const toMaterialSharePayloadApi = (
  materialSharePayload: MaterialSharePayload,
): MaterialSharePayloadApi => {
  return {
    AnoMes: materialSharePayload.yearMonth,
    Canal: materialSharePayload.channel,
    CodigoCentro: materialSharePayload.centerCode,
    CodigoMaterial: materialSharePayload.materialCode,
    EquipeVenda: materialSharePayload.teamCode,
    EscritorioVenda: materialSharePayload.officeCode,
    Organizacao: materialSharePayload.organization,
    RegiaoVenda: materialSharePayload.regionCode,
    Setor: materialSharePayload.sector,
    TipoOrdemVenda: materialSharePayload.salesOrderType,
    UnidadeNegocio: materialSharePayload.businessUnit,
  }
}

export const toMaterialShareResponse = (
  materialShareResponseApi: MaterialShareResponseApi,
): MaterialShareResponse => {
  return {
    colors: materialShareResponseApi.cores.map((item) => ({
      availableQuantity: item.quantidadeDisponivel,
      colorCode: item.codigoCor,
      level: item.nivelBloqueio,
    })),
  }
}

export const toMaterialLevelPayloadApi = (
  materialLevelPayload: MaterialLevelPayload,
): MaterialLevelPayloadApi => {
  return {
    AnoMes: materialLevelPayload.yearMonth,
    CodigoBMU: materialLevelPayload.bmuCode,
    CodigoCentro: materialLevelPayload.centerCode,
    CodigoEquipeVenda: materialLevelPayload.teamCode,
    CodigoEscritorioVenda: materialLevelPayload.officeCode,
    CodigoRegiaoVenda: materialLevelPayload.regionCode,
    Materiais: materialLevelPayload.materials?.map((item) => ({
      CodigoCentro: item.centerCode,
      CodigoMaterial: item.materialCode,
    })),
    NivelBloqueio: materialLevelPayload.level,
  }
}

export const toFairShareTrackingPayloadApi = (
  fairShareTrackingPayload: FairShareTrackingPayload,
): FairShareTrackingPayloadApi => {
  return {
    anoMes: fairShareTrackingPayload.yearMonth,
    codigoBMU: fairShareTrackingPayload.bmuCode,
    codigoCentro: fairShareTrackingPayload.centerCode,
    codigoCor: fairShareTrackingPayload.colorCode,
    codigoEquipe: fairShareTrackingPayload.teamCode,
    codigoEscritorio: fairShareTrackingPayload.officeCode,
    codigoMaterial: fairShareTrackingPayload.materialCode,
    codigoRegiao: fairShareTrackingPayload.regionCode,
    dataFim: formatDate(fairShareTrackingPayload.endDate, 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'),
    dataInicio: formatDate(fairShareTrackingPayload.startDate, 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'),
    paginaAtual: fairShareTrackingPayload.currentPage,
    registrosPorPagina: fairShareTrackingPayload.pageSize,
  }
}

export const toFairShareTrackingResponse = (
  fairShareTrackingResponseApi: FairShareTrackingResponseApi,
): FairShareTrackingResponse => {
  return {
    data: fairShareTrackingResponseApi?.dados
      ? fairShareTrackingResponseApi.dados.map((item) => ({
          businessUnitCode: item.codigoUnidadeNegocio,
          centerCode: item.codigoCentro,
          changeDetails: item.detalhesAlteracao,
          colorCode: item.codigoCor,
          colorDescription: item.descricaoCor,
          date: formatDate(item.dataHora, 'DD/MM/YYYY'),
          dateHour: item.dataHora,
          event: item.evento,
          materialCode: item.codigoMaterial,
          materialDescription: item.descricaoMaterial,
          officeCode: item.codigoEscritorioVenda,
          origin: item.origem,
          regionCode: item.codigoRegiaoVenda,
          teamCode: item.codigoEquipeVenda,
          time: formatDate(item.dataHora, 'HH[h]mm'),
          user: item.usuario,
          yearMonth: item.anoMes,
        }))
      : [],
    rowsCount: fairShareTrackingResponseApi?.rowscount || 0,
  }
}
